import { useEffect } from 'react';
import type Map from 'ol/Map';
import { mapNavigator, NAVIGATION_CONFIG } from '../services/MapNavigator';

/**
 * Hook that connects the MapNavigator service to an OpenLayers Map instance.
 * Sets up the map reference and configures view constraints (min/max zoom).
 *
 * This hook should be called once in the Map component after the map is initialized.
 * It also configures the OpenLayers View with the correct min/max zoom constraints
 * and ensures smooth zoom animations are enabled via the default interaction settings.
 *
 * **Validates: Requirements 30.3, 30.4, 30.5, 30.6, 31.1, 31.2, 31.3**
 *
 * @param map - The OpenLayers Map instance (or null if not yet initialized)
 */
export function useMapNavigation(map: Map | null): void {
  useEffect(() => {
    if (!map) {
      mapNavigator.setMap(null);
      return;
    }

    // Attach the map to the navigator service
    mapNavigator.setMap(map);

    // Configure view constraints
    const view = map.getView();
    view.setMinZoom(NAVIGATION_CONFIG.MIN_ZOOM);
    view.setMaxZoom(NAVIGATION_CONFIG.MAX_ZOOM);

    return () => {
      mapNavigator.setMap(null);
    };
  }, [map]);
}
